import _ from 'lodash'
import RequestError from './RequestError'
import api from '@/api'

/**
 * Sites control plane — every call goes to the Castr API (`/sites/*`), which
 * owns the plan gate, slug rules and the Workers KV publish. The dashboard
 * never talks to Cloudflare directly. Contract: internal/sites-control-plane-api.md.
 */
export default {
	getSites,
	getSitesCaps,
	checkSlug,
	suggestSlug,
	getSite,
	createSite,
	updateSite,
	deleteSite,
	publishSite,
	getHostnamesStatus,
}

/**
 * @return {Promise<{ sites: object[], caps: { enabled: boolean, maxSites: number, used: number } }>}
 */
function getSites() {
	return makeRequest('/sites')
}

function getSitesCaps() {
	return makeRequest('/sites/caps')
}

/**
 * Validity + availability of an explicit slug, with the resulting dummy host.
 * @param {string} slug
 */
function checkSlug(slug) {
	return makeRequest({
		path: '/sites/check-slug',
		params: { slug },
	})
}

/**
 * Server-derived free slug for a site name (same rules the create call uses).
 * @param {string} name
 */
function suggestSlug(name) {
	return makeRequest({
		path: '/sites/check-slug',
		params: { name },
	})
}

function getSite(siteId) {
	return makeRequest(`/sites/${siteId}`)
}

/**
 * `template` (`catalog` | `broadcast`, sites-broadcast-template-contract.md
 * §5) is always sent (no feature flag); castr defaults to `catalog`.
 * @param {{ name: string, slug?: string, theme?: object, template?: 'catalog'|'broadcast' }} payload
 */
function createSite(payload) {
	return makeRequest({
		method: 'post',
		path: '/sites',
		data: payload,
	})
}

/**
 * @param {string} siteId
 * A `template` change republishes the record at once (`templateChangedAt`
 * set); `409 reason: 'templates_disabled'` when the flag is off on castr.
 * @param {{ name?: string, theme?: object, status?: 'active'|'disabled', flags?: object, template?: 'catalog'|'broadcast' }} payload
 */
function updateSite(siteId, payload) {
	return makeRequest({
		method: 'put',
		path: `/sites/${siteId}`,
		data: payload,
	})
}

function deleteSite(siteId) {
	return makeRequest({
		method: 'delete',
		path: `/sites/${siteId}`,
	})
}

/**
 * Publish: runtime settings draft → live, a `draft` site becomes `active`,
 * paywall sync + KV republish (control-plane §6g). castr answers
 * `{ site, publishedKeys, publish, runtime }`; older castr answered the bare
 * Site, which is normalised into the same envelope.
 * @return {Promise<{ site: object, publishedKeys: string[], publish: object|null, runtime: object|null }>}
 */
async function publishSite(siteId) {
	const res = await makeRequest({
		method: 'post',
		path: `/sites/${siteId}/publish`,
	})
	if (res && res.site) return { publishedKeys: [], runtime: null, ...res }
	return { site: res, publishedKeys: [], publish: res?.publish || null, runtime: null }
}

/**
 * Whether the site's address really serves yet. A freshly registered Castr
 * address takes the App Platform edge 1–2 minutes (alias → certificate →
 * route); until then the host answers a bare 530. castr re-checks what is
 * stale (DO domain phase + an HTTPS probe) and answers
 * `{ host, ready, phase: registering|pending_dns|pending_ssl|active|failed, since, detail, startedAt, retry, hosts }`.
 * @param {string} siteId
 */
function getHostnamesStatus(siteId) {
	return makeRequest(`/sites/${siteId}/hostnames/status`)
}

/**
 * @param {RequestConfig|string} reqConfig
 */
async function makeRequest(reqConfig) {
	if (typeof reqConfig === 'string') {
		reqConfig = {
			path: reqConfig
		}
	}

	reqConfig.url = reqConfig.path

	let res
	try {
		res = await api.request(reqConfig)
	} catch (err) {
		const edata = _.get(err, 'response.data')
		const error = new RequestError(edata)
		// castr gates travel as Boom `reason` (+ extras such as `maxSites` / `used`)
		error.reason = edata?.reason || null
		error.payload = edata && typeof edata === 'object' ? edata : null
		throw error
	}

	return res.data
}
